import type { Theme } from "@mui/material";
import { DateField, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import type { BaseDateValidationProps } from "@mui/x-date-pickers/internals";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { isValid, parseISO } from "date-fns";
import type { FieldError } from "react-hook-form";

import {
  getDateFnsLocale,
  getDateFormat,
} from "Infrastructure/Utils/LocaleUtils";
import type { ExtractProps } from "Infrastructure/Utils/ObjectUtils";
import type { InputLabelProps } from "Shared/Inputs/InputLabel";
import { InputWrapper } from "Shared/Inputs/InputWrapper";

export type BlDatePickerProps = {
  value: Date | string | null | undefined;
  name?: string;
  disabledPast?: boolean;
  disabled?: boolean;
  readOnlyField?: boolean;
  required?: boolean;
  onChange?: (date: Date | null) => void;
  labelProps?: Omit<InputLabelProps, "htmlFor">;
  fieldError?: FieldError;
  fixedHeight?: boolean;
} & BaseDateValidationProps;

const dateFieldSx = (theme: Theme) => ({
  "& .MuiPickersInputBase-root": {
    backgroundColor: theme.colors.backgroundInput,
    boxSizing: "border-box",
    "&:before, &:after": { content: "none" },
    "& fieldset": {
      transition: "border-color 200ms",
      borderColor: theme.colors.border,
    },
    "&:hover:not(.Mui-disabled) fieldset": {
      borderColor: theme.palette.text.secondary,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: "1px",
    },
    "&.Mui-disabled": {
      backgroundColor: `${theme.palette.text.disabled}80`,
      "& fieldset": { borderColor: theme.colors.border },
    },
    "&.Mui-error fieldset": {
      borderColor: theme.palette.error.main,
    },
    "& .MuiIconButton-root": {
      color: theme.palette.common.white,
      opacity: 0.6,
    },
  },
  "& .MuiPickersInputBase-sectionsContainer": {
    height: 33,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.4,
    display: "flex",
    alignItems: "center",
  },
});

type DatePickerFieldProps = ExtractProps<typeof DateField>;
type FieldProps = DatePickerFieldProps & { readOnly?: boolean };

const Field = (props: FieldProps): React.ReactNode => {
  const { readOnly, ...rest } = props;
  return (
    <DateField
      readOnly={readOnly}
      variant="outlined"
      {...rest}
      sx={(theme) => dateFieldSx(theme)}
    />
  );
};

export const BlDatePicker = ({
  value,
  name,
  disabledPast,
  disabled,
  readOnlyField,
  onChange,
  required,
  labelProps,
  fieldError,
  fixedHeight,
  ...rest
}: BlDatePickerProps): React.ReactNode => {
  const hasError = !!fieldError;

  const handleChange = (date: Date | null) => {
    onChange?.(isValid(date) ? date : null);
  };

  return (
    <InputWrapper
      labelProps={
        labelProps?.label
          ? { ...labelProps, htmlFor: name, disabled, required }
          : undefined
      }
      errorProps={{ error: fieldError, fixedHeight }}
    >
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={getDateFnsLocale()}
      >
        <DatePicker
          className={hasError ? "Mui-error" : ""}
          format={getDateFormat()}
          value={
            typeof value === "string"
              ? isValid(parseISO(value))
                ? parseISO(value)
                : null
              : isValid(value)
                ? value
                : null
          }
          onChange={(e: unknown) => handleChange(e as Date)}
          disablePast={disabledPast}
          disabled={disabled}
          slots={{ field: Field }}
          slotProps={{
            field: { readOnly: readOnlyField },
            openPickerButton: { color: "inherit" },
            openPickerIcon: {
              color: "inherit",
              sx: { width: "22px", height: "22px" },
            },
          }}
          {...rest}
        />
      </LocalizationProvider>
    </InputWrapper>
  );
};
